import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { IconLinkButton } from '../../components/Button'
import { RADOM_COLORS } from '../../util/Constants'
import { Container } from '../../components/Animations'
import { errorToast, ROUTES, successToast, useTestMode } from '../../util/Util'
import Close from '../../icons/Close'
import NavbarController from '../../state/NavbarController'
import { getMethod, getPaymentMethods, ManagedPaymentMethod, prodNetworks, testNetworks } from '../../util/Managed'
import Radom, { CreatePayoutEntity, CreatePayoutRequest, Customer, ExternalAccount, PayoutFeeEstimate } from '../../state/Radom'
import SelectAssets from './PayoutsComponents/SelectAssets'
import PayoutDetails from './PayoutsComponents/PayoutDetails'
import PayoutReview from './PayoutsComponents/PayoutReview'
import { getChainById } from '../../util/Chains'
import Treasury from '../../state/Treasury'
import { Tokens } from '../../util/Tokens'
import { Currency } from '../../util/Currencies'
import { useNavigate } from 'react-router-dom'

enum PayoutType {
  Crypto = 'Crypto',
  Fiat = 'Fiat'
}

enum PayoutSteps {
  SelectAssets,
  SelectDetails,
  ReviewAndConfirm
}

const CreatePayout = (): JSX.Element => {
  const [testMode] = useTestMode()
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false)
  const [payoutType, setPayoutType] = useState<PayoutType>(PayoutType.Crypto)
  const [selectedFundingMethod, setSelectedFundingMethod] = useState<ManagedPaymentMethod>()
  const [selectedPayoutMethod, setSelectedPayoutMethod] = useState<ManagedPaymentMethod | Currency>()
  const [payoutAmount, setPayoutAmount] = useState<string>('')
  const [customers, setCustomers] = useState<Customer[]>([])
  const [customerFormVisible, setCustomerFormVisible] = useState(false)
  const [step, setStep] = useState<PayoutSteps>(PayoutSteps.SelectAssets)
  const navigate = useNavigate()

  // External account state:
  // - Fiat: required as destination (externalAccountId)
  // - Crypto: used as source for payoutAddress/network in request payload
  const [externalAccounts, setExternalAccounts] = useState<ExternalAccount[]>([])
  const [selectedExternalAccount, setSelectedExternalAccount] = useState<ExternalAccount | undefined>()
  const [isLoadingExternalAccounts, setIsLoadingExternalAccounts] = useState(false)

  // Fee estimate state
  const [feeEstimate, setFeeEstimate] = useState<PayoutFeeEstimate | null>(null)

  const canProceedToDetails = useMemo((): boolean => {
    // In this flow both fiat and crypto require funding method, customer, and a selected destination.
    return !!selectedFundingMethod &&
      !!selectedCustomer &&
      !!selectedExternalAccount
  }, [selectedCustomer, selectedFundingMethod, selectedExternalAccount])

  const chainName = useMemo((): string | undefined => {
    return (getChainById(selectedFundingMethod?.chainId ?? 0)?.name !== 'Unknown'
      ? getChainById(selectedFundingMethod?.chainId ?? 0)?.name
      : null) ||
            selectedFundingMethod?.hostChain?.name || selectedFundingMethod?.name
  }, [selectedFundingMethod])

  const availableBalance = useMemo(() => Treasury.managedBalances
    .filter(b => {
      const method = getMethod(b.network, b.token)
      return method.ticker === selectedFundingMethod?.ticker &&
             method.chainId === selectedFundingMethod?.chainId &&
             ((method.isTestnet && testMode) || (!method.isTestnet && !testMode))
    })
    .reduce((sum, b) => sum + Number(b.balance), 0), [Treasury.managedBalances, selectedFundingMethod, testMode])

  const decimals = useMemo(() => Tokens[selectedFundingMethod?.ticker || '']?.decimals[selectedFundingMethod?.chainId || ''] ??
                   Tokens[selectedFundingMethod?.ticker || '']?.decimals.default ?? 2, [selectedFundingMethod])

  const loadCustomers = useCallback(async (): Promise<void> => {
    setIsLoadingCustomer(true)
    try {
      const networks = testMode ? testNetworks : prodNetworks
      const c = await Radom.listCustomers(0, 100, networks)
      setCustomers(c.data)
      setCustomerFormVisible(false)
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoadingCustomer(false)
    }
  }, [testMode])

  // Load external accounts when customer changes
  const loadExternalAccounts = useCallback(async (customerId: string): Promise<void> => {
    setIsLoadingExternalAccounts(true)
    try {
      const accounts = await Radom.listExternalAccounts(customerId)
      setExternalAccounts(accounts)
    } catch (e) {
      console.error('Failed to load external accounts', e)
      setExternalAccounts([])
    } finally {
      setIsLoadingExternalAccounts(false)
    }
  }, [])

  useEffect(() => {
    if (selectedCustomer) {
      loadExternalAccounts(selectedCustomer.id)
    } else {
      setExternalAccounts([])
      setSelectedExternalAccount(undefined)
    }
  }, [selectedCustomer, loadExternalAccounts])

  const [isSubmitting, setIsSubmitting] = useState(false)

  const submitPayout = async (): Promise<void> => {
    if (!payoutAmount || !selectedCustomer || !selectedExternalAccount) return

    setIsSubmitting(true)
    try {
      let payoutEntry: CreatePayoutEntity

      if (payoutType === PayoutType.Fiat) {
        payoutEntry = {
          amount: payoutAmount,
          email: selectedCustomer.email || '',
          name: selectedCustomer.name || selectedCustomer.companyName || undefined,
          gateway: 'fiat',
          externalAccountId: selectedExternalAccount.id
        }
      } else {
        const crypto = selectedExternalAccount.crypto
        if (!crypto?.address || !crypto?.network) {
          errorToast('Selected payout address is missing network or address')
          return
        }

        const selectedFundingNetwork = chainName
        if (selectedFundingNetwork && selectedFundingNetwork !== crypto.network) {
          errorToast('Funding asset network must match the selected payout address network')
          return
        }

        const accountType = selectedCustomer.accountType === 'Business' || selectedCustomer.accountType === 'Individual'
          ? selectedCustomer.accountType
          : undefined

        payoutEntry = {
          amount: payoutAmount,
          customerId: selectedCustomer.id,
          accountType,
          email: selectedCustomer.email || '',
          name: selectedCustomer.name || selectedCustomer.companyName || undefined,
          gateway: 'crypto',
          payoutAddress: crypto.address,
          network: crypto.network,
          token: selectedFundingMethod?.hostChain?.tokenAddress
        }
      }

      const accountType = selectedExternalAccount?.accountOwnerType || selectedCustomer.accountType

      const req: CreatePayoutRequest = {
        payouts: [payoutEntry],
        accountType: accountType || undefined,
        deductFees: false,
        sendEmailReceipts: false
      }

      const createdPayout = await Radom.createPayout(req)

      // Auto-confirm the payout
      await Radom.confirmPayout(createdPayout.id)

      successToast('Payout created and confirmed successfully')
      navigate(ROUTES.PAYOUTS)
      clearInputs()
      setStep(PayoutSteps.SelectAssets)
    } catch (err: any) {
      console.error('Failed to create payout', err)
      errorToast(err?.response?.data?.error || err?.message || 'Failed to create payout')
    } finally {
      setIsSubmitting(false)
    }
  }

  const onContinue = (): void => {
    if (step === PayoutSteps.SelectAssets && !canProceedToDetails) return
    if (step === PayoutSteps.SelectDetails) {
      if (+payoutAmount === 0) return
      if (!selectedExternalAccount) return
      if (payoutType === PayoutType.Crypto && +payoutAmount > availableBalance) return
    }
    if (step === PayoutSteps.ReviewAndConfirm) {
      submitPayout()
      return
    }
    setStep(prev => prev + 1)
  }

  const getDefaultFiatFundingMethod = (): ManagedPaymentMethod | undefined => {
    return getPaymentMethods().find(
      m => m.ticker === 'USDC' && m.hostChain?.name === 'Ethereum' && !m.isTestnet
    )
  }

  const clearInputs = (newPayoutType?: PayoutType): void => {
    const type = newPayoutType ?? payoutType
    // For fiat: default to ETH USDC; for crypto: no default (user picks)
    setSelectedFundingMethod(type === PayoutType.Fiat ? getDefaultFiatFundingMethod() : undefined)
    setSelectedCustomer(null)
    setSelectedPayoutMethod(undefined)
    setPayoutAmount('')
    setSelectedExternalAccount(undefined)
    setExternalAccounts([])
    setFeeEstimate(null)
  }

  const onBack = (): void => {
    if (step === PayoutSteps.SelectDetails) {
      setPayoutAmount('')
    }
    setStep(prev => prev - 1)
  }
  useEffect(() => {
    NavbarController.setFullscreen(true)
    return () => NavbarController.setFullscreen(false)
  }, [])

  useEffect(() => {
    loadCustomers()
  }, [loadCustomers])

  useEffect(() => {
    clearInputs(payoutType)
  }, [payoutType])

  useEffect(() => {
    Treasury.loadBalances()
  }, [])

  const renderStep = (): JSX.Element => {
    switch (step) {
    case PayoutSteps.SelectAssets:
      return <SelectAssets
        testMode={testMode}
        payoutType={payoutType}
        setPayoutType={setPayoutType}
        selectedFundingMethod={selectedFundingMethod}
        setSelectedFundingMethod={setSelectedFundingMethod}
        setSelectedPayoutMethod={setSelectedPayoutMethod}
        selectedPayoutMethod={selectedPayoutMethod}
        isLoadingCustomer={isLoadingCustomer}
        customers={customers}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        customerFormVisible={customerFormVisible}
        setCustomerFormVisible={setCustomerFormVisible}
        loadCustomers={loadCustomers}
        onContinue={onContinue}
        externalAccounts={externalAccounts}
        selectedExternalAccount={selectedExternalAccount}
        setSelectedExternalAccount={setSelectedExternalAccount}
        isLoadingExternalAccounts={isLoadingExternalAccounts}
      />

    case PayoutSteps.SelectDetails:
      return <PayoutDetails
        payoutType={payoutType}
        chainName={chainName}
        selectedFundingMethod={selectedFundingMethod}
        selectedPayoutMethod={selectedPayoutMethod}
        selectedCustomer={selectedCustomer}
        onContinue={onContinue}
        payoutAmount={payoutAmount}
        setPayoutAmount={setPayoutAmount}
        onBack={onBack}
        availableBalance={availableBalance}
        decimals={decimals}
        externalAccounts={externalAccounts}
        selectedExternalAccount={selectedExternalAccount}
        setSelectedExternalAccount={setSelectedExternalAccount}
        feeEstimate={feeEstimate}
        setFeeEstimate={setFeeEstimate}
      />
    case PayoutSteps.ReviewAndConfirm:
      return <PayoutReview
        payoutType={payoutType}
        testMode={testMode}
        selectedFundingMethod={selectedFundingMethod}
        selectedPayoutMethod={selectedPayoutMethod}
        selectedCustomer={selectedCustomer}
        onContinue={onContinue}
        payoutAmount={payoutAmount}
        onBack={onBack}
        networkName={chainName}
        isSubmitting={isSubmitting}
        selectedExternalAccount={selectedExternalAccount}
        feeEstimate={feeEstimate}
      />

    default:
      return <></>
    }
  }

  return (
    <Container style={{
      padding: '0 20px 24px',
      gap: 20,
      flexDirection: 'column',
      alignItems: 'center',
      boxSizing: 'border-box',
      overflow: 'visible',
      width: '100%'
    }}>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: `1px solid ${RADOM_COLORS.GRAY_MED}`,
        padding: '12px 20px',
        width: '100%',
        minHeight: 68,
        background: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <IconLinkButton to={ROUTES.PAYOUTS} type="button" style={{
            padding: 5,
            cursor: 'pointer'
          }} >
            <Close style={{ width: 15, pointerEvents: 'none' }} />
          </IconLinkButton>
          <span style={{ fontSize: 18 }}>Create payout</span>
        </div>

      </div>
      {renderStep()}
    </Container>

  )
}

export default CreatePayout
